import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection, query, where, getDocs, addDoc, serverTimestamp, doc, updateDoc
} from "firebase/firestore";
import { SIDO_LIST, SIGUNGU_LIST } from "./regionData";

// 볼링만 지원
const SPORT_SETS = { "볼링": 3 }; // 볼링은 3게임
const SPORTS = ["볼링"]; // 볼링만

function Match({ acceptedMatchInfo }) {
  const [user, setUser] = useState(null);

  // 사용자 정보 로드
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);
  }, []);
  const [mode, setMode] = useState("랭킹전");
  const [sport, setSport] = useState("볼링"); // 기본값 볼링
  const [sido, setSido] = useState("");
  const [sigungu, setSigungu] = useState("");
  const [dateRows, setDateRows] = useState([
    { date: "", startAmPm: "오전", startHour: "09", endAmPm: "오전", endHour: "11" }
  ]);
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [pendingMatchId, setPendingMatchId] = useState(null);
  const [gameScores, setGameScores] = useState([{ my: "", opp: "" }, { my: "", opp: "" }, { my: "", opp: "" }]); // 3게임
  const [resultStep, setResultStep] = useState(null);
  const [msg, setMsg] = useState("");

  const isReady =
    sport && sido && sigungu &&
    dateRows.every(r =>
      r.date && r.startHour && r.endHour && r.startAmPm && r.endAmPm
    ) &&
    (mode === "랭킹전" || (mode === "친선전" && selectedFriend));

  useEffect(() => {
    if (mode === "친선전") {
      (async () => {
        const q = query(collection(db, "friends"), where("userId", "==", user?.id));
        const snap = await getDocs(q);
        setFriends(snap.docs.map(d => d.data().friendId));
      })();
    }
  }, [mode, user?.id]);

  const addDateRow = () =>
    setDateRows([...dateRows, { date: "", startAmPm: "오전", startHour: "09", endAmPm: "오전", endHour: "11" }]);
  const removeDateRow = idx =>
    setDateRows(dateRows.length > 1 ? dateRows.filter((_, i) => i !== idx) : dateRows);

  const handleScoreChange = (gameIdx, type, value) => {
    const newScores = [...gameScores];
    newScores[gameIdx][type] = value;
    setGameScores(newScores);
  };

  const handleDateChange = (idx, key, value) =>
    setDateRows(rows => rows.map((row, i) => i === idx ? { ...row, [key]: value } : row));

  const handleRankingMatch = async () => {
    if (!isReady) return setMsg("모든 정보를 입력하세요!");
    try {
      await addDoc(collection(db, "matches"), {
        type: "랭킹전",
        sport: "볼링", // 볼링 고정
        playerA: user.id,
        region: { sido, sigungu },
        dates: dateRows,
        status: "pending",
        createdAt: serverTimestamp()
      });
      setMsg("🎳 볼링 랭킹전 매칭 신청 완료! 상대를 기다려주세요.");
    } catch (error) {
      console.error("랭킹전 매칭 실패:", error);
      setMsg("매칭 신청에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleFriendlyMatch = async () => {
    if (!isReady) return setMsg("친구, 지역, 날짜/시간 모두 입력하세요!");
    try {
      const matchRef = await addDoc(collection(db, "matches"), {
        type: "친선전",
        sport: "볼링", // 볼링 고정
        playerA: user.id,
        playerB: selectedFriend,
        region: { sido, sigungu },
        dates: dateRows,
        status: "pending",
        createdAt: serverTimestamp(),
        scores: [],
      });
      await addDoc(collection(db, "notifications"), {
        from: user.id,
        to: selectedFriend,
        sport: "볼링",
        type: "friendly_match_request",
        status: "pending",
        matchId: matchRef.id,
        createdAt: serverTimestamp(),
      });
      setWaiting(true);
      setPendingMatchId(matchRef.id);
      setMsg("🎳 볼링 친선전 요청을 기다리는 중입니다!");
    } catch (error) {
      console.error("친선전 매칭 실패:", error);
      setMsg("친선전 신청에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleSubmitScore = async () => {
    if (gameScores.some(s => s.my === "" || s.opp === "")) {
      setMsg("모든 게임의 점수를 입력하세요.");
      return;
    }
    
    // 볼링 점수 유효성 검사 (0-300점)
    for (let i = 0; i < gameScores.length; i++) {
      const myScore = parseInt(gameScores[i].my);
      const oppScore = parseInt(gameScores[i].opp);
      if (myScore < 0 || myScore > 300 || oppScore < 0 || oppScore > 300) {
        setMsg(`${i+1}게임 점수가 유효하지 않습니다. (0-300점 입력)`);
        return;
      }
    }

    try {
      await updateDoc(doc(db, "matches", pendingMatchId), {
        scores: gameScores,
        status: "score_wait"
      });
      setResultStep("대기");
      setMsg("점수가 전송되었습니다. 상대가 확인 중입니다!");
      await addDoc(collection(db, "notifications"), {
        from: user.id,
        to: acceptedMatchInfo.opponent,
        sport: "볼링",
        type: "friendly_score_check",
        status: "pending",
        matchId: pendingMatchId,
        createdAt: serverTimestamp(),
        scores: gameScores
      });
    } catch (error) {
      console.error("점수 제출 실패:", error);
      setMsg("점수 제출에 실패했습니다. 다시 시도해주세요.");
    }
  };

  if (!user) {
    return (
      <div style={{
        maxWidth: 520, margin: "40px auto", padding: 40, 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", 
        borderRadius: 24, boxShadow: "0 8px 32px rgba(102,126,234,0.25)",
        textAlign: "center", color: "#fff"
      }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🎳</div>
        <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 16 }}>
          Sportier 볼링 매칭
        </div>
        <div style={{ fontSize: 18, opacity: 0.9, lineHeight: 1.6 }}>
          로그인/회원가입 후 볼링 매칭 서비스를<br/>
          이용하실 수 있습니다.
        </div>
        <button 
          onClick={() => window.location.href = '/login'}
          style={{
            marginTop: 24,
            background: "rgba(255,255,255,0.2)",
            color: "#fff",
            border: "2px solid rgba(255,255,255,0.3)",
            borderRadius: 12,
            padding: "12px 32px",
            fontSize: 16,
            fontWeight: 600,
            cursor: "pointer",
            backdropFilter: "blur(10px)"
          }}
        >
          로그인하러 가기
        </button>
      </div>
    );
  }

  if (acceptedMatchInfo && acceptedMatchInfo.type === "친선전") {
    const { opponent, sport, region, dates } = acceptedMatchInfo;
    return (
      <div style={mainCard}>
        <h1 style={mainTitle}>🎳 볼링 친선전 결과 입력</h1>
        <div style={matchInfoCard}>
          <div style={{ 
            background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
            color: "#fff",
            padding: "16px",
            borderRadius: 12,
            marginBottom: 20,
            textAlign: "center"
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              친선전 매칭 정보
            </div>
            <div>🎯 상대: <span style={{ fontWeight: 600 }}>{opponent}</span></div>
            <div>🎳 종목: <span style={{ fontWeight: 600 }}>볼링</span></div>
            <div>📍 장소: <span style={{ fontWeight: 600 }}>{region.sido} {region.sigungu}</span></div>
            {dates.map((d, idx) => (
              <div key={idx}>
                📅 일시: {d.date} / {d.startAmPm} {d.startHour}:00 ~ {d.endAmPm} {d.endHour}:00
              </div>
            ))}
          </div>
          
          <div style={{ 
            background: "#f8fafc", 
            padding: "20px", 
            borderRadius: 12, 
            marginBottom: 16 
          }}>
            <div style={{ 
              fontSize: 18, 
              fontWeight: 700, 
              color: "#1e40af", 
              marginBottom: 16,
              textAlign: "center"
            }}>
              🎳 볼링 점수 입력 (3게임)
            </div>
            
            {gameScores.map((score, i) => (
              <div key={i} style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 12, 
                margin: "12px 0",
                background: "#fff",
                padding: "12px",
                borderRadius: 8,
                border: "2px solid #e2e8f0"
              }}>
                <span style={{ 
                  minWidth: "60px", 
                  fontWeight: 700, 
                  color: "#374151",
                  fontSize: 16
                }}>
                  {i + 1}게임
                </span>
                <input
                  type="number"
                  value={score.my}
                  onChange={e => handleScoreChange(i, "my", e.target.value)}
                  placeholder="내 점수"
                  style={{
                    ...scoreInput,
                    borderColor: score.my && (parseInt(score.my) < 0 || parseInt(score.my) > 300) ? "#ef4444" : "#d1d5db"
                  }}
                  min={0}
                  max={300}
                />
                <span style={{ fontWeight: 700, fontSize: 18, color: "#6b7280" }}>VS</span>
                <input
                  type="number"
                  value={score.opp}
                  onChange={e => handleScoreChange(i, "opp", e.target.value)}
                  placeholder="상대 점수"
                  style={{
                    ...scoreInput,
                    borderColor: score.opp && (parseInt(score.opp) < 0 || parseInt(score.opp) > 300) ? "#ef4444" : "#d1d5db"
                  }}
                  min={0}
                  max={300}
                />
              </div>
            ))}
            
            <div style={{ 
              fontSize: 13, 
              color: "#6b7280", 
              textAlign: "center", 
              marginTop: 12 
            }}>
              ※ 볼링 점수는 0점부터 300점까지 입력 가능합니다.
            </div>
          </div>
          
          <button style={mainBtn} onClick={handleSubmitScore}>
            🎳 점수 제출하기
          </button>
        </div>
        {msg && <div style={msgStyle}>{msg}</div>}
      </div>
    );
  }

  return (
    <div style={mainCard}>
      <h1 style={mainTitle}>
        🎳 볼링 {mode} 매칭
      </h1>
      
      <div style={{ 
        background: "linear-gradient(135deg, #fef3c7, #fde68a)",
        border: "2px solid #f59e0b",
        borderRadius: 12,
        padding: "16px",
        marginBottom: 24,
        textAlign: "center"
      }}>
        <div style={{ 
          fontSize: 16, 
          fontWeight: 700, 
          color: "#92400e",
          marginBottom: 4
        }}>
          🎯 Sportier 볼링 전용 매칭 서비스
        </div>
        <div style={{ fontSize: 14, color: "#a16207" }}>
          개인전 랭킹은 볼링 종목만 지원됩니다
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        <button 
          onClick={() => setMode("랭킹전")} 
          style={mode === "랭킹전" ? tabActive : tab}
        >
          🏆 랭킹전
        </button>
        <button 
          onClick={() => setMode("친선전")} 
          style={mode === "친선전" ? tabActive : tab}
        >
          🤝 친선전
        </button>
      </div>

      {/* 볼링 종목 고정 표시 */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: 12, 
        marginBottom: 24,
        background: "#f0f9ff",
        padding: "16px",
        borderRadius: 12,
        border: "2px solid #0ea5e9"
      }}>
        <div style={{ 
          fontSize: 40,
          background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
          borderRadius: 12,
          padding: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          🎳
        </div>
        <div>
          <div style={{ 
            fontSize: 20, 
            fontWeight: 800, 
            color: "#0c4a6e" 
          }}>
            볼링
          </div>
          <div style={{ 
            fontSize: 14, 
            color: "#0369a1" 
          }}>
            3게임 토탈 스코어 방식
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 20 }}>
        <span style={{ color: "#2778ef", fontSize: 18, fontWeight: 700 }}>🌐 선호지역</span>
        <select value={sido} onChange={e => { setSido(e.target.value); setSigungu(""); }} style={selectBox}>
          <option value="">광역시/도</option>
          {SIDO_LIST.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={sigungu} onChange={e => setSigungu(e.target.value)} style={selectBox} disabled={!sido}>
          <option value="">시/군/구</option>
          {(SIGUNGU_LIST[sido] || []).map(sg => <option key={sg}>{sg}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 20 }}>
        <span style={{ color: "#7154fa", fontSize: 18, fontWeight: 700 }}>📅 가능 날짜/시간</span>
        {dateRows.map((r, idx) => (
          <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0" }}>
            <input 
              type="date" 
              value={r.date} 
              onChange={e => handleDateChange(idx, "date", e.target.value)} 
              style={dateInput} 
            />
            <select 
              value={r.startAmPm} 
              onChange={e => handleDateChange(idx, "startAmPm", e.target.value)} 
              style={selectTime}
            >
              <option>오전</option>
              <option>오후</option>
            </select>
            <select 
              value={r.startHour} 
              onChange={e => handleDateChange(idx, "startHour", e.target.value)} 
              style={selectTime}
            >
              {[...Array(12)].map((_,i)=> <option key={i+1}>{String(i+1).padStart(2,"0")}</option>)}
            </select>
            <span style={{ fontWeight: 600 }}>~</span>
            <select 
              value={r.endAmPm} 
              onChange={e => handleDateChange(idx, "endAmPm", e.target.value)} 
              style={selectTime}
            >
              <option>오전</option>
              <option>오후</option>
            </select>
            <select 
              value={r.endHour} 
              onChange={e => handleDateChange(idx, "endHour", e.target.value)} 
              style={selectTime}
            >
              {[...Array(12)].map((_,i)=> <option key={i+1}>{String(i+1).padStart(2,"0")}</option>)}
            </select>
            {dateRows.length > 1 && (
              <button onClick={() => removeDateRow(idx)} style={minusBtn}>－</button>
            )}
          </div>
        ))}
        <button onClick={addDateRow} style={plusBtn}>+ 날짜 추가</button>
      </div>

      {mode === "친선전" && (
        <div style={{ marginBottom: 24 }}>
          <span style={{ color: "#2778ef", fontSize: 17, fontWeight: 700 }}>👫 친구 선택</span>
          <select value={selectedFriend} onChange={e => setSelectedFriend(e.target.value)} style={selectBox}>
            <option value="">친구를 선택하세요</option>
            {friends.map(fid => <option key={fid} value={fid}>{fid}</option>)}
          </select>
          {friends.length === 0 && (
            <div style={{ 
              fontSize: 14, 
              color: "#6b7280", 
              marginTop: 8 
            }}>
              친구를 먼저 추가해주세요!
            </div>
          )}
        </div>
      )}

      {!waiting && (
        <button
          style={{
            ...mainBtn,
            background: isReady 
              ? "linear-gradient(135deg, #2563eb, #1d4ed8)" 
              : "linear-gradient(135deg, #9ca3af, #6b7280)",
            cursor: isReady ? "pointer" : "not-allowed"
          }}
          onClick={mode === "랭킹전" ? handleRankingMatch : handleFriendlyMatch}
          disabled={!isReady}
        >
          {mode === "랭킹전" ? "🏆 볼링 랭킹전 매칭 신청" : "🤝 볼링 친선전 매칭 신청"}
        </button>
      )}

      {mode === "친선전" && waiting && (
        <div style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          color: "#92400e",
          fontWeight: 700,
          padding: 20,
          borderRadius: 16,
          marginTop: 20,
          textAlign: "center",
          border: "2px solid #f59e0b"
        }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>⏳ 대기 중</div>
          <div>{selectedFriend} 님의 볼링 친선전 응답을 기다리고 있습니다!</div>
        </div>
      )}

      {msg && (
        <div style={{
          color: msg.includes("실패") || msg.includes("오류") ? "#dc2626" : "#2563eb",
          marginTop: 20,
          textAlign: "center",
          fontWeight: 700,
          background: msg.includes("실패") || msg.includes("오류") ? "#fef2f2" : "#eff6ff",
          padding: "12px",
          borderRadius: 8,
          border: `2px solid ${msg.includes("실패") || msg.includes("오류") ? "#fecaca" : "#bfdbfe"}`
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}

// 스타일 정의
const mainCard = { 
  maxWidth: 550, 
  margin: "40px auto", 
  padding: 36, 
  background: "#fff", 
  borderRadius: 24, 
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)" 
};

const mainTitle = { 
  fontSize: 28, 
  fontWeight: 900, 
  marginBottom: 28, 
  color: "#1e40af", 
  display: "flex", 
  alignItems: "center", 
  gap: 12 
};

const tab = { 
  flex: 1, 
  fontSize: 18, 
  fontWeight: 800, 
  borderRadius: 12, 
  padding: "14px 0", 
  border: "2px solid #e2e8f0", 
  background: "#f8fafc", 
  color: "#64748b", 
  cursor: "pointer",
  transition: "all 0.2s"
};

const tabActive = { 
  ...tab, 
  background: "linear-gradient(135deg, #2563eb, #1d4ed8)", 
  color: "#fff",
  borderColor: "#1d4ed8",
  boxShadow: "0 4px 12px rgba(37,99,235,0.3)"
};

const selectBox = { 
  fontSize: 16, 
  borderRadius: 8, 
  padding: "10px 14px", 
  border: "2px solid #d1d5db", 
  minWidth: 120, 
  marginLeft: 8,
  background: "#fff"
};

const selectTime = { 
  fontSize: 15, 
  borderRadius: 6, 
  padding: "6px 10px", 
  border: "1px solid #d1d5db", 
  marginLeft: 4,
  background: "#fff"
};

const dateInput = { 
  fontSize: 15, 
  borderRadius: 6, 
  padding: "6px 12px", 
  border: "1px solid #d1d5db", 
  marginLeft: 8, 
  marginRight: 10,
  background: "#fff"
};

const mainBtn = { 
  width: "100%", 
  background: "linear-gradient(135deg, #2563eb, #1d4ed8)", 
  color: "#fff", 
  fontWeight: 900, 
  fontSize: 18, 
  border: "none", 
  borderRadius: 16, 
  marginTop: 24, 
  padding: "16px 0", 
  boxShadow: "0 4px 16px rgba(37,99,235,0.3)", 
  cursor: "pointer", 
  display: "flex", 
  alignItems: "center", 
  justifyContent: "center", 
  gap: 12,
  transition: "all 0.2s"
};

const msgStyle = { 
  marginTop: 16, 
  textAlign: "center", 
  fontWeight: 700,
  padding: "12px",
  borderRadius: 8
};

const matchInfoCard = { 
  margin: "20px 0", 
  padding: 20, 
  borderRadius: 16, 
  background: "#fafcff", 
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)", 
  fontSize: 16, 
  fontWeight: 600 
};

const scoreInput = { 
  width: 80, 
  fontSize: 16, 
  borderRadius: 6, 
  border: "2px solid #d1d5db", 
  padding: "8px 12px", 
  textAlign: "center",
  background: "#fff"
};

const plusBtn = { 
  margin: "8px 0 0 0", 
  background: "#f0f9ff", 
  color: "#0ea5e9", 
  fontWeight: 800, 
  borderRadius: 8, 
  border: "2px solid #bae6fd", 
  padding: "6px 16px", 
  fontSize: 14, 
  cursor: "pointer",
  transition: "all 0.2s"
};

const minusBtn = { 
  ...plusBtn, 
  color: "#ef4444", 
  border: "2px solid #fecaca",
  background: "#fef2f2"
};

export default Match;